import { Checkbox, TextArea } from '@zep-us/zus';
import { CheckIcon, ChevronRightIcon } from '@zep-us/zus-icons';
import { cn } from '@/utils/tailwind.util';
import { useAiBuilderChatCopy } from '../hooks/useAiBuilderChatCopy';
import { type AiBuilderPendingQuestionSetQuestion } from '../store/types';
export type AiBuilderQuestionAnswerControlsProps = {
  question: AiBuilderPendingQuestionSetQuestion;
  directInputSelected: boolean;
  selectedOptionIds: readonly string[];
  directText: string;
  locked?: boolean;
  unavailableOptionIds?: readonly string[];
  onSelectedOptionIdsChange: (optionIds: readonly string[]) => void;
  onDirectInputSelectedChange: (selected: boolean) => void;
  onDirectTextChange: (text: string) => void;
};
const EMPTY_OPTION_IDS: readonly string[] = [];
const DIRECT_INPUT_MAX_LENGTH = 100;
const ACHIEVEMENT_STANDARD_CODE_PATTERN = /^\d{1,2}[^\s[\]-]+(?:-\d{2}){1,2}$/u;
export const AiBuilderQuestionAnswerControls = ({
  question,
  directInputSelected,
  selectedOptionIds,
  directText,
  locked = false,
  unavailableOptionIds = EMPTY_OPTION_IDS,
  onSelectedOptionIdsChange,
  onDirectInputSelectedChange,
  onDirectTextChange
}: AiBuilderQuestionAnswerControlsProps) => {
  const copy = useAiBuilderChatCopy();
  const unavailableOptionIdSet = new Set(unavailableOptionIds);
  const selectedOptionIdSet = new Set(selectedOptionIds);
  const choiceControls = question.selectionMode === 'multiple' ? <div role="group" aria-label={copy.choice.multipleSelectionAriaLabel} aria-disabled={locked} className="flex min-w-0 flex-col gap-4xs">
        {question.options.map(option => {
      const selected = selectedOptionIdSet.has(option.optionId);
      const unavailable = unavailableOptionIdSet.has(option.optionId);
      const presentation = getAnswerOptionPresentation(question, option);
      return <label key={option.optionId} className={optionShellClassName({
        hasDescription: Boolean(presentation.description),
        locked,
        selected,
        unavailable
      })}>
              <Checkbox name={`ai-builder-question-${question.questionId}`} checked={selected} disabled={locked || unavailable} aria-label={presentation.ariaLabel} onCheckedChange={checked => {
          const nextOptionIds = new Set(selectedOptionIds);
          if (checked === true) nextOptionIds.add(option.optionId);else nextOptionIds.delete(option.optionId);
          onSelectedOptionIdsChange(question.options.map(candidate => candidate.optionId).filter(optionId => nextOptionIds.has(optionId)));
        }} />
              <AnswerOptionContent label={presentation.label} description={presentation.description} selected={selected} disabled={locked || unavailable} />
            </label>;
    })}
      </div> : <div role="group" aria-label={copy.choice.singleSelectionAriaLabel} aria-disabled={locked} className="flex min-w-0 flex-col gap-4xs">
        {question.options.map(option => {
      const selected = !directInputSelected && selectedOptionIdSet.has(option.optionId);
      const unavailable = unavailableOptionIdSet.has(option.optionId);
      const presentation = getAnswerOptionPresentation(question, option);
      return <button key={option.optionId} type="button" disabled={locked || unavailable} aria-pressed={selected} aria-label={presentation.ariaLabel} className={cn('group', optionShellClassName({
        hasDescription: Boolean(presentation.description),
        locked,
        selected,
        unavailable
      }))} onClick={() => onSelectedOptionIdsChange([option.optionId])}>
              <AnswerOptionContent label={presentation.label} description={presentation.description} selected={selected} disabled={locked || unavailable} />
              <SingleChoiceTrailingIcon selected={selected} disabled={locked || unavailable} />
            </button>;
    })}
      </div>;
  return <div className="flex min-w-0 flex-col gap-4xs" data-sentry-component="AiBuilderQuestionAnswerControls" data-sentry-source-file="AiBuilderQuestionAnswerControls.tsx">
      {question.options.length > 0 && choiceControls}
      {question.allowDirectInput && (question.selectionMode === 'multiple' ? <div className={optionShellClassName({
      hasDescription: true,
      locked,
      selected: directInputSelected,
      unavailable: false
    })} onClick={event => {
      const target = event.target as HTMLElement;
      if (!locked && !directInputSelected && !target.closest('textarea, input, button, [role="checkbox"]')) {
        onDirectInputSelectedChange(true);
      }
    }}>
            <Checkbox name={`ai-builder-question-${question.questionId}`} checked={directInputSelected} disabled={locked} aria-label={copy.questionSet.directInput.label} onCheckedChange={checked => {
        onDirectInputSelectedChange(checked === true);
      }} />
            <DirectInputEditor active={directInputSelected} value={directText} locked={locked} ariaLabel={copy.questionSet.directInput.ariaLabel} placeholder={directInputSelected ? copy.questionSet.directInput.placeholder : copy.questionSet.directInput.label} onActivate={() => onDirectInputSelectedChange(true)} onChange={onDirectTextChange} />
          </div> : <div className={cn('group', optionShellClassName({
      hasDescription: true,
      locked,
      selected: directInputSelected,
      unavailable: false
    }))} onClick={event => {
      const target = event.target as HTMLElement;
      if (!locked && !directInputSelected && !target.closest('textarea, input, button')) {
        onDirectInputSelectedChange(true);
      }
    }}>
            <DirectInputEditor active={directInputSelected} value={directText} locked={locked} ariaLabel={copy.questionSet.directInput.ariaLabel} placeholder={directInputSelected ? copy.questionSet.directInput.placeholder : copy.questionSet.directInput.label} onActivate={() => onDirectInputSelectedChange(true)} onChange={onDirectTextChange} />
            <SingleChoiceTrailingIcon selected={directInputSelected} disabled={locked} />
          </div>)}
    </div>;
};
const SingleChoiceTrailingIcon = ({
  selected,
  disabled
}: {
  selected: boolean;
  disabled: boolean;
}) => <span aria-hidden="true" data-testid="single-choice-trailing-icon" className="flex size-lg shrink-0 items-center justify-center" data-sentry-component="SingleChoiceTrailingIcon" data-sentry-source-file="AiBuilderQuestionAnswerControls.tsx">
    {selected ? <CheckIcon size="sm" fill="currentColor" /> : !disabled && <ChevronRightIcon size="sm" className="text-neutral opacity-0 transition-opacity duration-150 ease-out group-hover:opacity-100" />}
  </span>;
const DirectInputEditor = ({
  active,
  value,
  locked,
  ariaLabel,
  placeholder,
  onActivate,
  onChange
}: {
  active: boolean;
  value: string;
  locked: boolean;
  ariaLabel: string;
  placeholder: string;
  onActivate: () => void;
  onChange: (text: string) => void;
}) => <TextArea.InputField value={value} disabled={locked} required={false} rows={1} autoFocus={active} aria-label={ariaLabel} placeholder={placeholder} maxLength={DIRECT_INPUT_MAX_LENGTH} autoSize minRows={1} maxRows={3} className={cn('h-auto min-h-6 w-auto min-w-0 flex-1 whitespace-pre-wrap break-words bg-transparent p-0 text-body-md-semibold leading-6 text-default placeholder:text-assistive', locked && 'text-disabled placeholder:text-disabled')} onClick={() => {
  if (!active && !locked) onActivate();
}} onKeyDown={event => {
  if (!active && !locked && (event.key === 'Enter' || event.key === ' ')) {
    event.preventDefault();
    onActivate();
  }
}} onChange={event => {
  if (!active && !locked) onActivate();
  if (!locked) {
    onChange(event.target.value.slice(0, DIRECT_INPUT_MAX_LENGTH));
  }
}} data-sentry-element="unknown" data-sentry-component="DirectInputEditor" data-sentry-source-file="AiBuilderQuestionAnswerControls.tsx" />;
const getAnswerOptionPresentation = (question: AiBuilderPendingQuestionSetQuestion, option: AiBuilderPendingQuestionSetQuestion['options'][number]) => {
  const canonicalValue = option.value?.trim() ?? '';
  const isAchievementStandard = question.missingSlot === 'topic' && ACHIEVEMENT_STANDARD_CODE_PATTERN.test(canonicalValue);
  if (!isAchievementStandard) {
    return {
      ariaLabel: option.label,
      label: option.label,
      description: option.description
    };
  }
  const codeLabel = `[${canonicalValue}]`;
  return {
    ariaLabel: `${codeLabel}, ${option.label}`,
    label: codeLabel,
    description: option.label
  };
};
const optionShellClassName = ({
  hasDescription,
  locked,
  selected,
  unavailable
}: {
  hasDescription: boolean;
  locked: boolean;
  selected: boolean;
  unavailable: boolean;
}) => cn('flex w-full min-w-0 gap-3xs rounded bg-default px-sm py-xs text-left transition-colors duration-150 ease-out', hasDescription ? 'items-start' : 'items-center', !selected && !locked && !unavailable && 'hover:bg-neutral', selected && !locked && !unavailable && 'bg-strong-alternative text-strong hover:bg-strong-alternative', (locked || unavailable) && 'cursor-not-allowed bg-neutral text-disabled hover:bg-neutral');
const AnswerOptionContent = ({
  label,
  description,
  selected,
  disabled
}: {
  label: string;
  description: string | null;
  selected: boolean;
  disabled: boolean;
}) => <span className="flex min-w-0 flex-1 flex-col gap-4xs" data-sentry-component="AnswerOptionContent" data-sentry-source-file="AiBuilderQuestionAnswerControls.tsx">
    <span className={cn('min-w-0 whitespace-normal break-words text-body-md-semibold text-default', selected && 'text-strong', disabled && 'text-disabled')}>
      {label}
    </span>
    {description && <span className={cn('min-w-0 whitespace-normal break-words text-body-sm-medium text-neutral', disabled && 'text-disabled')}>
        {description}
      </span>}
  </span>;